import { useState } from 'react';

import { ApiError } from '@/api/client';
import { useSession } from '@/store/session';
import { Icon, type IconName } from '@/components/ui/Icon';

/** One-time. Name and date of birth are not editable afterwards, so say so. */
export function FirstLogin() {
  const complete = useSession((s) => s.completeFirstLogin);
  const [name, setName] = useState('Explorer');
  const [dob, setDob] = useState('2000-01-01');
  const [country, setCountry] = useState('MM');
  const [locale, setLocale] = useState('en');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    const clean = name.trim().replace(/\s+/g, '');
    if (!/^[A-Za-z0-9_-]{3,20}$/.test(clean)) {
      setError('Use 3-20 letters, digits, hyphen or underscore.');
      return;
    }
    setBusy(true);
    try {
      await complete({ display_name: clean, date_of_birth: dob, country_code: country, locale });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto h-full w-full max-w-[520px] overflow-y-auto px-3 pb-6 pt-6">
      <div className="text-center">
        <h1 className="font-pixel text-2xl text-coin [text-shadow:0_3px_0_#8A5A0E]">SHWETAP</h1>
        <p className="mt-2 text-[10px] uppercase tracking-widest text-ink-dim">by ReiwaSakura.Tech</p>
      </div>

      <section className="mt-5 rounded-md border border-[var(--hairline)] bg-[var(--surface)] p-3.5">
        <h2 className="font-pixel text-[9px] text-torch-core">WELCOME, EXPLORER</h2>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
          Nobody has ever reached the bottom of this cave. Set your name once — it cannot be
          changed later.
        </p>

        <Field label="Game name (3-20 characters, no spaces)">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={20}
                 className="w-full rounded-lg border border-[var(--hairline)] bg-black/35 p-2.5" />
        </Field>
        <Field label="Date of birth">
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                 className="w-full rounded-lg border border-[var(--hairline)] bg-black/35 p-2.5" />
        </Field>
        <div className="flex gap-2">
          <Field label="Country">
            <select value={country} onChange={(e) => setCountry(e.target.value)}
                    className="w-full rounded-lg border border-[var(--hairline)] bg-black/35 p-2.5">
              <option value="MM">Myanmar</option><option value="TH">Thailand</option>
              <option value="SG">Singapore</option><option value="JP">Japan</option>
              <option value="XX">Other</option>
            </select>
          </Field>
          <Field label="Language">
            <select value={locale} onChange={(e) => setLocale(e.target.value)}
                    className="w-full rounded-lg border border-[var(--hairline)] bg-black/35 p-2.5">
              <option value="en">English</option><option value="my">မြန်မာ</option>
            </select>
          </Field>
        </div>
      </section>

      <section className="mt-3 rounded-md border border-[var(--hairline)] bg-[var(--surface)] p-3.5">
        <h2 className="font-pixel text-[9px] text-torch-core">THE OLD MINER</h2>
        <p className="mt-2 text-[11px] italic leading-relaxed text-ink-dim">
          "Welcome... Nobody has reached the end. Take this axe. Take this torch. Good luck."
        </p>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {([
            ['axe', 'WOOD AXE'], ['torch', 'CANDLE'], ['coin', '250 COINS'],
          ] as [IconName, string][]).map(([icon, label]) => (
            <span key={label}
                  className="flex items-center gap-1 rounded-md border border-current
                             px-2 py-1 font-pixel text-[7px] text-coin">
              <Icon name={icon} size={11} />
              {label}
            </span>
          ))}
        </div>
      </section>

      {error && <p role="alert" className="mt-3 text-center text-xs text-[var(--danger)]">{error}</p>}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy}
        className="mt-4 w-full rounded-sm border border-[rgba(255,220,170,.5)]
                   bg-[linear-gradient(#FFA945,#E0761C)] py-3.5 font-extrabold text-[#2A1503]
                   shadow-[0_4px_0_-1px_#8C440C] active:translate-y-0.5 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Enter the story'}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mt-2.5 block">
      <span className="text-[11px] text-ink-dim">{label}</span>
      <span className="mt-1 block">{children}</span>
    </label>
  );
}
